import type { ComputedRef, Ref } from "vue";
import { computed, onMounted, ref, watch } from "vue";

import { registerDrag } from "@/components/gestures/drag";
import { sendStickyNoteAction } from "@/composables/useEventBus";
import { relativeClientCoord } from "@/math/coordinate-utils";
import type { Card } from "@/model/card";
import { useBoardStore } from "@/store/board";
import { useDraggingStore } from "@/store/dragging";
import { linkBetween, useLinkStore } from "@/store/link";
import { isLinkDrag } from "@/utils/dom/dom";

import { linkDragHandler } from "./linkDragHandler";

interface Props {
  el: Ref<HTMLElement | null>;
  card: Card;
  disabled?: Ref<Readonly<boolean>>;
}

interface UseLinkDragReturn {
  isLinkingTarget: ComputedRef<Readonly<boolean>>;
  isLinkingSource: ComputedRef<Readonly<boolean>>;
}

export function useLinkDrag({ el, card, disabled }: Props): UseLinkDragReturn {
  onMounted(() => {
    if (disabled?.value) return;
    el?.value?.addEventListener("pointerenter", handlePointerEnter);
    el.value?.addEventListener("pointerdown", handlePointerDown);
  });

  const linkStore = useLinkStore();
  const boardStore = useBoardStore();

  // the sticky note is being linked to
  const isLinkingTarget = computed(() => linkStore.linking.to?.id === card.id);
  // the sticky note is being linked from
  const isLinkingSource = computed(() => linkStore.linking.from?.id === card.id);
  const enlargeAfterLinking = ref(false);

  watch(isLinkingTarget, (val) => {
    if (val) {
      el?.value?.addEventListener("pointerleave", handlePointerLeave, { once: true });
    }
  });

  // used to enlarge the sticky note after linking
  const handleDocumentPointerUp = () => {
    if (!el.value) return;

    // this sends the enlarge event
    onLinkDragEnd();
  };

  const handlePointerDown = (event: PointerEvent) => {
    if (disabled?.value || !isLinkDrag(event)) return;

    if (boardStore.enlargedStickyNoteId === card.id) {
      enlargeAfterLinking.value = true;
      boardStore.setEnlargedStickyNoteId(null);

      useDraggingStore().startLinkDragging(card.id, event.pointerId, relativeClientCoord(event));

      document.addEventListener("pointerup", handleDocumentPointerUp, { once: true });
    }

    registerDrag(event, linkDragHandler(event, card, onLinkDragEnd));
  };

  const onLinkDragEnd = () => {
    if (enlargeAfterLinking.value) {
      enlargeAfterLinking.value = false;
      sendStickyNoteAction(card.id, { action: "enlarge", focus: true, trigger: "after-link-drag" });
    }
  };

  const handlePointerEnter = () => {
    if (
      !disabled?.value &&
      linkStore.linking.from?.id &&
      !linkStore.isLinkedFrom(card.id) &&
      !linkBetween(card, linkStore.linking.from)
    ) {
      useLinkStore().setLinkingTarget({ id: card.id, type: "sticky" });
    }
  };

  const handlePointerLeave = () => {
    if (linkStore.linking.to?.id === card.id) {
      useLinkStore().resetLinkingTarget();
    }
  };

  return { isLinkingTarget, isLinkingSource };
}
